/**
 * One-time sign-up onboarding. Shown by App when an authenticated NON-
 * master user hasn't completed their profile (me.profileComplete === false).
 * Collects the sign-up fields, PUTs /api/profile, and on success the slice
 * flips profileComplete → the gate releases and the app renders. The
 * master account never sees this (profile_complete is always true).
 */
import { useState } from "react";
import type { FormEvent } from "react";
import { useAppDispatch, useAppSelector } from "@/state/hooks";
import { saveProfile } from "@/state/slices/meSlice";
import styles from "./Onboarding.module.css";

const EXPERIENCE = ["Beginner", "Intermediate", "Advanced", "Professional"];
const REFERRAL = [
  "Search engine",
  "Social media",
  "YouTube",
  "Friend or colleague",
  "Online community / forum",
  "Other",
];

function detectTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch {
    return "";
  }
}

export function Onboarding() {
  const dispatch = useAppDispatch();
  const meName = useAppSelector((s) => s.me.name);
  const meEmail = useAppSelector((s) => s.me.email);

  const [name, setName] = useState(meName);
  const [phone, setPhone] = useState("");
  const [country, setCountry] = useState("");
  const [experience, setExperience] = useState("");
  const [referral, setReferral] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const timezone = detectTimezone();
  const valid =
    !!name.trim() &&
    !!phone.trim() &&
    !!country.trim() &&
    !!experience &&
    !!referral;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!valid || submitting) return;
    setSubmitting(true);
    setError(null);
    const res = await dispatch(
      saveProfile({
        name: name.trim(),
        phone: phone.trim(),
        country: country.trim(),
        timezone,
        experience,
        referral_source: referral,
      }),
    );
    if (saveProfile.fulfilled.match(res)) {
      // profileComplete is now true → App releases the gate, no nav needed.
      return;
    }
    setSubmitting(false);
    setError("Couldn't save your details. Please try again.");
  }

  return (
    <div className={styles.page}>
      <form className={styles.card} onSubmit={onSubmit}>
        <div className={styles.brand}>
          <img src="/logo-64.png" alt="" className={styles.brandLogo} />
          Pulsa<span>rix</span>
        </div>
        <h1 className={styles.title}>Welcome — tell us a bit about you</h1>
        <p className={styles.sub}>
          A few quick details to finish setting up your account
          {meEmail ? ` (${meEmail})` : ""}.
        </p>

        <label className={styles.field}>
          <span>Full name</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name"
            autoComplete="name"
          />
        </label>

        <label className={styles.field}>
          <span>Phone number</span>
          <input
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+1 555 000 1234"
            inputMode="tel"
            autoComplete="tel"
          />
        </label>

        <label className={styles.field}>
          <span>Country</span>
          <input
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            placeholder="Country"
            autoComplete="country-name"
          />
        </label>

        <label className={styles.field}>
          <span>Trading experience</span>
          <select
            value={experience}
            onChange={(e) => setExperience(e.target.value)}
          >
            <option value="" disabled>
              Select…
            </option>
            {EXPERIENCE.map((x) => (
              <option key={x} value={x}>
                {x}
              </option>
            ))}
          </select>
        </label>

        <label className={styles.field}>
          <span>How did you hear about us?</span>
          <select value={referral} onChange={(e) => setReferral(e.target.value)}>
            <option value="" disabled>
              Select…
            </option>
            {REFERRAL.map((x) => (
              <option key={x} value={x}>
                {x}
              </option>
            ))}
          </select>
        </label>

        {error ? <div className={styles.error}>{error}</div> : null}

        <button
          className={styles.submit}
          type="submit"
          disabled={!valid || submitting}
        >
          {submitting ? "Saving…" : "Continue to Pulsarix"}
        </button>
        <p className={styles.legal}>
          By continuing you agree to our{" "}
          <a href="/terms" target="_blank" rel="noopener noreferrer">
            Terms of Service
          </a>{" "}
          and{" "}
          <a href="/privacy" target="_blank" rel="noopener noreferrer">
            Privacy Policy
          </a>
          . We record how you use the app to improve it; you can turn this off
          at any time in Help.
        </p>
        {timezone ? (
          <div className={styles.tz}>Timezone detected: {timezone}</div>
        ) : null}
      </form>
    </div>
  );
}
